import {
  AggregationPeriodData,
  KnownHistoricDeliveryStatsAggregationPeriod,
  KnownHistoricDeliveryStatsEventTypes
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { Card, Grid } from '@mui/material';
import dayjs, { ManipulateType } from 'dayjs';
import { useMemo } from 'react';
import { BarChart, BarChartProps } from '../../../../ui/components/pure/charts/bar/bar-chart.component';
import { useDashboardStatsContext } from '../../context/use-dashboard-stats.context';
import { eventTypeColors, historicOrderStatsEventTypeToLabel } from '../../dashboard.constants';
import { EventHistoryHeader } from './event-history-header/event-history-header.component';
import { useEventHistoryStyles } from './event-history.styles';

export function EventHistory() {
  const classes = useEventHistoryStyles();
  const { historicStats } = useDashboardStatsContext();

  const { labels, figures } = useMemo(() => {
    const eventTypeData = historicStats.data.data?.[historicStats.state.eventType];
    return buildBarChartData(
      eventTypeData?.data,
      historicStats.state.fromDate,
      historicStats.state.toDate,
      historicStats.state.aggPeriod
    );
  }, [
    historicStats.data.data,
    historicStats.state.aggPeriod,
    historicStats.state.eventType,
    historicStats.state.fromDate,
    historicStats.state.toDate
  ]);

  const { barChartDataLabel, barColor } = useMemo(
    () => setBarChartOptions(historicStats.state.eventType),
    [historicStats.state.eventType]
  );

  const { tooltipTitleCallBack, xAxisTickCallback } = useMemo(
    () => setBarChartDisplayCallbacks(historicStats.state.aggPeriod),
    [historicStats.state.aggPeriod]
  );

  const barChartData = useMemo<Pick<BarChartProps, 'data'>['data']>(
    () => ({
      labels,
      datasets: [
        {
          label: barChartDataLabel,
          data: figures,
          backgroundColor: barColor
        }
      ]
    }),
    [barChartDataLabel, barColor, figures, labels]
  );

  return (
    <Card className={classes.card}>
      <Grid container>
        <EventHistoryHeader />
        <Grid container>
          <BarChart
            isLoading={historicStats.data.isLoading}
            data={barChartData}
            xAxisTickCallback={xAxisTickCallback}
            tooltipTitleCallBack={tooltipTitleCallBack}
            hideTickLabelsOnMobile
            hideVerticalGridlinesOnMobile
          />
        </Grid>
      </Grid>
    </Card>
  );
}

// These predicate functions are passed to the ChartJS "Bar" component
// ChartJS calls the predicate function for each element in the data array passing in
// the index of the element (in the case of x-axis). The "BarChart" component wraps this callback with its own callback that
// converts the index to the actual label (in this case the date) and passes that value into the predicate
// If the predicate returns undefined, the tick, tick label and grid line will be hidden
const xAxisTickPredicatesByPeriod: Record<
  KnownHistoricDeliveryStatsAggregationPeriod,
  BarChartProps['xAxisTickCallback']
> = {
  day(period) {
    const date = dayjs(period);
    const weekStartingDate = dayjs(date).startOf('week');
    if (date.isSame(weekStartingDate, 'day')) {
      return date.format('D MMM');
    } else {
      return undefined;
    }
  },
  week(period: string) {
    const date = dayjs(period);
    const monthStartDate = dayjs(date).startOf('month');
    let startOfFirstWeekOfMonth = dayjs(monthStartDate).startOf('week');
    if (date.isSame(startOfFirstWeekOfMonth, 'day')) {
      return date.format(dateDisplayFormats[KnownHistoricDeliveryStatsAggregationPeriod.Week]);
    }
    const nextMonthStartDate = monthStartDate.add(1, 'month');
    const startOfFirstWeekOfNextMonth = dayjs(nextMonthStartDate).startOf('week');
    if (startOfFirstWeekOfNextMonth.isAfter(dayjs(), 'day')) {
      return undefined;
    }
    if (date.isSame(startOfFirstWeekOfNextMonth, 'day')) {
      return date.format(dateDisplayFormats[KnownHistoricDeliveryStatsAggregationPeriod.Week]);
    }
    return undefined;
  },
  weekday(_period: string) {
    return undefined;
  },
  month(period: string) {
    return dayjs(period).format(dateDisplayFormats[KnownHistoricDeliveryStatsAggregationPeriod.Month]);
  },
  year(_period: string) {
    return undefined;
  }
};

// Sets the predicates for determining which ticks and gridlines to show/hide and how to format dates
function setBarChartDisplayCallbacks(
  aggPeriod: KnownHistoricDeliveryStatsAggregationPeriod
): Pick<BarChartProps, 'xAxisTickCallback' | 'tooltipTitleCallBack'> {
  return {
    xAxisTickCallback: xAxisTickPredicatesByPeriod[aggPeriod],
    tooltipTitleCallBack(item) {
      if (item) {
        return dayjs(item.label).format(dateDisplayFormats[aggPeriod]);
      }
      return '';
    }
  };
}

// Set the tooltip label (Bookings, Deliveries etc) and bar color based on eventType
function setBarChartOptions(eventType: KnownHistoricDeliveryStatsEventTypes) {
  return {
    barChartDataLabel: historicOrderStatsEventTypeToLabel[eventType],
    barColor: eventTypeColors[eventType]
  } as const;
}

// Calls the two helper functions below to create a list of dates and a corresponding list of values
function buildBarChartData(
  data: AggregationPeriodData[] | undefined,
  fromDate: string,
  toDate: string,
  aggPeriod: KnownHistoricDeliveryStatsAggregationPeriod
) {
  const result: { labels: string[]; figures: number[] } = {
    labels: buildPeriodMap(fromDate, toDate, aggPeriod),
    figures: []
  };
  if (typeof data === 'undefined') {
    result.figures = result.labels.map(() => 0);
    return result;
  }

  result.figures = mapDataByPeriod(data, result.labels, aggPeriod);

  return result;
}

// Creates an array with an element for each date between the from date and to date according to the aggPeriod
function buildPeriodMap(
  fromDateStr: string,
  toDateStr: string,
  aggPeriod: KnownHistoricDeliveryStatsAggregationPeriod
) {
  const fromDate = dayjs(fromDateStr);
  const toDate = dayjs(toDateStr);

  let range = aggPeriod;

  if (aggPeriod === KnownHistoricDeliveryStatsAggregationPeriod.Weekday) {
    range = KnownHistoricDeliveryStatsAggregationPeriod.Week;
  }

  if (fromDate.isAfter(toDate)) {
    return [];
  }

  if (fromDate.isSame(toDate)) {
    return [fromDate.toISOString()];
  }

  let prevDate = fromDate.toISOString();
  const periods = [prevDate];

  while (dayjs(prevDate).isBefore(toDate, 'day')) {
    const nextDate = dayjs(prevDate).add(1, range as ManipulateType);
    // Don't add the nextDate if it is after today
    if (!nextDate.isAfter(dayjs(), 'day')) {
      periods.push(nextDate.toISOString());
    }
    prevDate = nextDate.toISOString();
  }

  return periods;
}

// Creates an array of equal length to the array of periods above, containing the value from the
// data for each period
function mapDataByPeriod(
  data: AggregationPeriodData[],
  periods: string[] /* ISO strings */,
  aggPeriod: KnownHistoricDeliveryStatsAggregationPeriod
) {
  return periods.map((period) => {
    const formattedPeriodString = dayjs(period).format(dateParsingFormats[aggPeriod]);
    const periodData = data.find((d) => {
      let date = d.period;
      if (date.includes('~')) {
        const [start] = date.split('~');
        date = start;
      }
      return date === formattedPeriodString;
    });
    return periodData?.count || 0;
  });
}

// These are the formats the HistoricOrderStats endpoint uses for each aggPeriod
const dateParsingFormats: Record<KnownHistoricDeliveryStatsAggregationPeriod, string> = {
  day: 'YYYY-MM-DD',
  week: 'YYYY-MM-DD',
  weekday: 'YYYY-MM-DD',
  month: 'YYYY-MM',
  year: 'YYYY'
};

// These are the formats used to display dates from each aggPeriod on the chart
const dateDisplayFormats: Record<KnownHistoricDeliveryStatsAggregationPeriod, string> = {
  day: 'D MMM',
  week: '[W/C] D MMM',
  weekday: '[W/C] D MMM',
  month: 'MMM YY',
  year: 'YYYY'
};
